import React, { useState } from "react";
import {
  Box,
  Card,
  IconButton,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import {
  Subscription,
  SubscriptionOnboardingTypeEnum,
  SubscriptionStatusEnum,
  CACHE_KEYS,
  useUpdateSubscription,
} from "@rentablo/api/apiv1";
import TrashBin from "@icons/myicons/trash-bin-delete.svg?react";
import { ConfirmDeleteDialog } from "@components";
import FnzLogo from "@images/bank-logo/fnz.png";
import FondsdepotLogo from "../../../images/bank-logo/fondsdepot.png";
import FilLogo from "../../../images/bank-logo/FIL.png";
import DwsLogo from "../../../images/bank-logo/DWS.png";
import DabLogo from "../../../images/bank-logo/DAB.png";
import ComdirectLogo from "../../../images/bank-logo/comdirect.png";
import MorgenFundLogo from "../../../images/bank-logo/morgenfund.png";
import { CashbackStatus } from "./CashbackStatus";
import { CashbackDashboardItemControls } from "./CashbackDashboardItemControls";

interface Props {
  subscription: Subscription;
}

const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  background: ${({ theme }) => theme.palette.common.white};
  border: 1px solid ${({ theme }) => theme.palette.border.primary};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  width: 56px;
  height: 56px;
  img {
    width: 56px;
  }
  ${({ theme }) => theme.breakpoints.down("md")} {
    width: 40px;
    height: 40px;
    img {
      width: 40px;
    }
  }
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const BodyContainer = styled.div`
  display: flex;
  justify-content: space-between;
  ${({ theme }) => theme.breakpoints.down("md")} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const TextInfo = styled.div`
  padding: ${({ theme }) => theme.spacing(0, 3)};
`;

const MainFlex = styled.div`
  display: flex;
  order: 1;
  ${({ theme }) => theme.breakpoints.down("md")} {
    order: 2;
  }
`;
const StatusContainer = styled.div`
  order: 2;
  ${({ theme }) => theme.breakpoints.down("md")} {
    display: flex;
    width: 100%;
    justify-content: space-between;
    order: 1;
    padding-bottom: ${({ theme }) => theme.spacing(4)};
  }
`;

const StyledCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing(3, 4)};
`;

export interface BankLogoProps {
  defaultBic?: string;
}

const BankLogo = (props: BankLogoProps) => {
  const { defaultBic } = props;
  const logoByDefaultBic = (bic?: string) => {
    switch (bic) {
      case "COBADEHDXXX":
        return ComdirectLogo;
      case "DABBDEMMXXX":
        return DabLogo;
      case "DEUTDEFFXXX":
        return DwsLogo;
      case "AUGBDE77XXX":
      case "COBADEMXXXX":
        return FnzLogo;
      case "FFBKDEFFTHK":
        return FilLogo;
      case "FODBDE77XXX":
        return FondsdepotLogo;
      case "DEUTLU2DXXX":
        return MorgenFundLogo;
      default:
        return ComdirectLogo;
    }
  };

  return (
    <ImageWrapper>
      <img src={logoByDefaultBic(defaultBic)} alt="cashback-logo" />
    </ImageWrapper>
  );
};

const getBankName = (name?: string) => {
  if (name === "ebase") return "FNZ";
  return name;
};

const DeleteButton = ({
  onClick,
  isFetching,
}: {
  onClick: () => void;
  isFetching: boolean;
}) => {
  const theme = useTheme();

  return (
    <IconButton
      size="small"
      onClick={onClick}
      color="tertiary"
      disabled={isFetching}
      sx={{
        marginTop: theme.spacing(3),
        [theme.breakpoints.down("md")]: {
          marginTop: theme.spacing(0),
        },
      }}
    >
      <TrashBin width="20px" height="20px" />
    </IconButton>
  );
};

const CashbackDashboardItem = (props: Props) => {
  const { subscription } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate, isPending } = useUpdateSubscription({ throwOnError: true });
  const { enqueueSnackbar } = useSnackbar();
  const [deleteModalShow, setDeleteModalShow] = useState(false);

  const handleDelete = () =>
    mutate(
      {
        id: subscription.id!,
        createOrUpdateSubscriptionParams: {
          status: "cancelled",
          isCancelled: true,
        },
      },
      {
        onSuccess: () => {
          setDeleteModalShow(false);
          enqueueSnackbar(t("domains.cashback.dashboard.item.deleteSuccess"), {
            variant: "success",
            autoHideDuration: 3000,
          });
          queryClient.refetchQueries({
            queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_SUBSCRIPTIONS],
          });
        },
      }
    );

  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const showDeleteButton = (status: SubscriptionStatusEnum | undefined) =>
    status !== "processed" &&
    status !== "contract_terminated" &&
    status !== "internal_processing" &&
    status !== "processing_at_bank";

  const isInternal =
    subscription.onboardingType === SubscriptionOnboardingTypeEnum.INTERNAL ||
    !subscription.onboardingType;

  return (
    <StyledCard>
      <ConfirmDeleteDialog
        isOpen={deleteModalShow}
        title={t("navigation.deleteModal.confirmTitle")}
        isDeleteRequestLoading={isPending}
        onClose={() => setDeleteModalShow(false)}
        onDelete={handleDelete}
      />
      <div>
        <BodyContainer>
          <MainFlex>
            <Box flexShrink={0}>
              <BankLogo defaultBic={subscription?.bank?.defaultBic} />
            </Box>

            <TextInfo>
              <Typography
                variant="overline"
                color="textSecondary"
                component="p"
              >
                {getBankName(subscription.bank?.name)}
              </Typography>
              <Typography variant="body1" fontWeight="600">
                Ihre Cashback Buchung
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                fontWeight="600"
                component="p"
              >
                {`${t("domains.cashback.dashboard.item.depositNumber")}: ${
                  subscription.primaryAccountIdentifier || "N/A"
                } • ${subscription.accountHolderReference?.firstName || ""} ${
                  subscription.accountHolderReference?.lastName || ""
                }`}
              </Typography>
            </TextInfo>
          </MainFlex>
          <StatusContainer>
            <CashbackStatus
              status={subscription.status!}
              isExternal={!isInternal}
            />
            {isSm && showDeleteButton(subscription.status) && (
              <DeleteButton onClick={handleDelete} isFetching={isPending} />
            )}
          </StatusContainer>
        </BodyContainer>
      </div>
      <ControlsContainer>
        {isInternal ? (
          <CashbackDashboardItemControls
            status={subscription.status!}
            subscriptionId={subscription.id!}
          />
        ) : (
          <Box />
        )}

        {!isSm && showDeleteButton(subscription.status) && (
          <DeleteButton
            onClick={() => setDeleteModalShow(true)}
            isFetching={isPending}
          />
        )}
      </ControlsContainer>
    </StyledCard>
  );
};

export default CashbackDashboardItem;
